// admin/src/pages/ResultContent.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TopNav from '../components/TopNav';
import ResultContentCardPro from '../components/ResultContentCardPro';
import ResultContentEditModal from '../components/ResultContentEditModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useAdminStore } from '../context/adminStore';
import { useAdminAuth } from '../auth/AdminAuthContext';

export default function ResultContent() {
  const { state, api } = useAdminStore();
  const { resultContents, filterText, loading, error } = state;
  const { secret } = useAdminAuth();
  const [localFilter, setLocalFilter] = useState(filterText);

  // Modal states
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const didFetchRef = useRef(false);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    api.loadResultContents();
  }, [api]);

  const filtered = useMemo(() => {
    if (!localFilter) return resultContents;
    const q = localFilter.toLowerCase();
    return resultContents.filter(r =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.tag || '').toLowerCase().includes(q)
    );
  }, [resultContents, localFilter]);

  // Handlers
  const handleNew = () => {
    setEditingItem(null);
    setShowEditModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleSave = async (data) => {
    await api.saveResultContent(data);
    // Modal will close automatically on success
  };

  const handleDeleteClick = (item) => {
    setDeletingItem(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    try {
      await api.deleteResultContent(deletingItem._id);
      setShowDeleteConfirm(false);
      setDeletingItem(null);
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingItem(null);
  };

  return (
    <>
      <TopNav />
      <div className="page-pad">
        <header className="adm-card__header" style={{ border: 'none', padding: 0, marginBottom: 10 }}>
          <h1 style={{ margin: 0 }}>Result Content</h1>
        </header>

        <div className="admin-toolbar">
          <input
            className="input"
            placeholder="Filter by title or tag…"
            value={localFilter}
            onChange={e => setLocalFilter(e.target.value)}
          />
          <button className="btn" onClick={() => api.setFilter(localFilter)}>Apply</button>
          <button
            className="btn btn--primary"
            onClick={handleNew}
          >
            + New Card
          </button>
        </div>

        {loading && <div className="muted">Loading…</div>}
        {error && <div className="error">{error}</div>}

        <div className="admin-grid">
          {filtered.map(rc => (
            <ResultContentCardPro
              key={rc._id}
              item={rc}
              onEdit={() => handleEdit(rc)}
              onDelete={() => handleDeleteClick(rc)}
            />
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="adm-empty" style={{ textAlign: 'center', padding: 40 }}>
            {localFilter ? 'No content matches your filter' : 'No result content yet'}
          </div>
        )}
      </div>

      <ResultContentEditModal
        item={editingItem}
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        onSave={handleSave}
        tenantId={state.tenantId}
        adminSecret={secret}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Result Content"
        message={`Are you sure you want to delete "${deletingItem?.title}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        danger={true}
      />
    </>
  );
}